/**
 * Authentication Types
 */

import type { ProfileStatus } from './member.types'

export enum UserRole {
    MEMBER = 'member',
    CURATOR = 'curator',
    ADMINISTRATOR = 'admin',
    CHAPTER_LEAD = 'chapter_lead',
}

/**
 * EasyNav — Guided Onboarding Navigation
 */
export type EasyNavPersona = 'organizer' | 'member' | 'partner'

export interface EasyNavPrefs {
    enabled: boolean               // master on/off toggle
    persona: EasyNavPersona | null // chosen or auto-deduced persona
    seen_welcome: boolean          // true after first visit (suppress auto-redirect)
    dismissed_pages: string[]      // route names where EasyNavPanel is hidden
}

export interface UserProfile {
    id: string
    auth_id?: string | null  // FK to auth.users, null for pending profiles
    email: string
    name: string | null
    avatar_url: string | null
    roles: UserRole[]
    status?: ProfileStatus  // 'pending' for pre-imported, 'active' for signed up users
    home_chapter_id: string | null
    created_at: string
    updated_at: string
    joined?: string | null
    daily_token_limit?: number | null // Per-profile LLM token limit (overrides global)

    // Profile fields
    current_company: string | null
    description: string | null
    website_url: string | null
    twitter_url: string | null
    linkedin_url: string | null
    telegram: string | null
    wallet_address: string | null
    expertise: string[]
    interests: string[]

    // Privacy settings
    is_public: boolean
    show_email: boolean
    show_socials: boolean
    show_wallet?: boolean
    available_for_matching?: boolean

    // MCP (AI assistant) access
    mcp_enabled?: boolean
    mcp_discoverable?: boolean

    // Onboarding
    onboarding_completed: boolean

    // LLM settings
    llm_mode: 'community' | 'personal' | 'local'
    personal_llm_endpoint: string | null
    personal_llm_provider: string | null
    personal_llm_model: string | null
    personal_llm_api_key_set: boolean // Don't expose actual key
    personal_llm_api_key_encrypted: string | null // Encrypted API key stored in DB
    encryption_key: string | null // Per-user encryption key

    // Badges array covering DAIAA and other system badges
    badges: string[]

    // Legacy import tracking
    legacy_id?: string | null
    agent_prefs?: Record<string, any> | null
}

export interface Session {
    access_token: string
    refresh_token: string
    expires_in: number
    expires_at?: number
    token_type: string
    user: AuthUser
}

export interface AuthUser {
    id: string
    email: string
    email_confirmed_at?: string
    phone?: string
    created_at: string
    updated_at: string
    app_metadata: {
        provider?: string
        providers?: string[]
    }
    user_metadata: {
        avatar_url?: string
        email?: string
        full_name?: string
        name?: string
        picture?: string
        [key: string]: any
    }
}

export interface SignUpCredentials {
    email: string
    password: string
    name?: string
}

export interface SignInCredentials {
    email: string
    password: string
}

export type OAuthProvider = 'google' | 'github' | 'discord' | 'twitter'

export interface AuthState {
    user: AuthUser | null
    profile: UserProfile | null
    session: Session | null
    isAuthenticated: boolean
    isLoading: boolean
    error: string | null
}
